/**
 * The control that opened the dock, so a close can hand focus back to it. A ref owned by a provider, exposed to
 * both ends through a small dedicated context — the same shape as `use-artifact-panel.ts`, and for the same
 * reason: the launcher that records it and the dock that reads it sit in different subtrees, so neither can hold
 * the value and neither can be handed the other's callback as a prop.
 *
 * Its own context rather than the chat controller's: this is a DOM node with a focus lifetime, not conversation
 * state, and the controller's context value is memoised against per-token re-renders it has no business in.
 *
 * Outside the provider the recorder no-ops and the reader falls straight through to the live-launcher query.
 * That is reachable and deliberate rather than defensive — `KellyChatGate` renders the app bare when its boundary
 * catches a Kelly crash, and the launcher gates on entitlement rather than on a controller, so it still renders
 * there. `KellyDock` does not, so nothing in that state ever asks for focus back.
 */

import { createContext, useCallback, useContext, useRef } from "react"
import type { ReactNode, RefObject } from "react"

/**
 * Marks an element as a dock launcher, so a close can find one that is actually in the document when the
 * remembered node is gone. Spread onto the control rather than written out, so the attribute the query below
 * looks for and the attribute the launchers carry cannot drift apart.
 */
export const DOCK_OPENER_ATTRIBUTES = { "data-kelly-dock-opener": "" } as const
const DOCK_OPENER_SELECTOR = "[data-kelly-dock-opener]"

const DockOpenerContext = createContext<RefObject<HTMLElement | null> | null>(null)

/** Mounted by `KellyChatGate` above every branch, so the launchers and the dock share one ref on every route. */
export function DockOpenerProvider({ children }: { children: ReactNode }) {
  const openerRef = useRef<HTMLElement | null>(null)
  return <DockOpenerContext.Provider value={openerRef}>{children}</DockOpenerContext.Provider>
}

/**
 * Called by a launcher as it OPENS the dock. Open only — a close records nothing, or "cleared on use" is not
 * true and a later close focuses a control from an older session.
 */
export function useRememberDockOpener(): (element: HTMLElement | null) => void {
  const openerRef = useContext(DockOpenerContext)
  return useCallback((element: HTMLElement | null) => {
    if (openerRef != null) openerRef.current = element
  }, [openerRef])
}

/**
 * Hands focus back to the control that opened the dock. Called only from a user-initiated close, never from
 * `/kelly-ai`'s restore-on-leave — a programmatic close during navigation must not yank focus out of the page
 * the user just moved to. Cleared on use so a later close cannot focus a control from an older session.
 *
 * The remembered node is often not focusable by the time it is needed: the launcher is mounted in five shell
 * headers with exactly one in the document at a time, so a section change detaches it, and `/kelly-ai` opens the
 * dock by dispatching the view directly, with no launcher involved at all. A bare `focus()` silently no-ops in
 * both cases and the dock's `inert` then hands focus to the document.
 */
export function useFocusDockOpener(): () => void {
  const openerRef = useContext(DockOpenerContext)
  return useCallback(() => {
    const opener = openerRef?.current ?? null
    if (openerRef != null) openerRef.current = null
    if (opener?.isConnected) {
      opener.focus()
      return
    }
    document.querySelector<HTMLElement>(DOCK_OPENER_SELECTOR)?.focus()
  }, [openerRef])
}
